import React from 'react';
import { Container, Box, Paper, Divider } from '@mui/material';
import { Typography } from '@mui/joy';

const About = () => {
    return (
        <Container maxWidth='lg'>
            <Box my={2}>
                <Paper elevation={0} style={{ padding: '20px' }}>
                    <Box>
                        <Typography level='h3' gutterBottom>
                            TravelGreen
                        </Typography>
                        <Typography level='body-sm'>
                        A research project by the <a href='https://www.cm.in.tum.de/en/home/'>Chair of Connected Mobility</a> at the TUM School of Computation, Information and Technology.
                        </Typography>
                    </Box>
                    <Divider />
                    <Box my={2}>
                        <Typography level='h4'>Objective</Typography>
                        <Typography level='body-sm'>
                            The aim of TravelGreen is to explore how digital trip-planning tools can promote sustainable tourism.
                            This study examines user decision-making processes and the balance between environmental,
                            social, and economic sustainability. By understanding the trade-offs users make when choosing
                            travel options, the research seeks to identify effective methods to encourage eco-friendly
                            choices without compromising user satisfaction.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Methodological Framework</Typography>
                        <Typography level='body-sm'>
                            The methodology enables users to enter their starting location, preferred travel month, and interests, allowing the algorithm to
                            calculate sustainable travel routes by analyzing CO2 emissions, destination popularity, and seasonality. The app sorts
                            recommendations based on emissions, popularity, seasonal demand, interests, and saved history, presenting them in an
                            interactive card or comparison format. Indicators like "Green Recommended" or "Hotspot Alert" nudge users toward
                            sustainable options. Visual aids, including carbon footprint graphs, cost breakdowns, and sustainability scores, help
                            users understand the environmental impact of their choices. Users can also create accounts to save trips and enjoy a
                            personalized experience.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Anticipated Outcomes</Typography>
                        <Typography level='body-sm'>
                            The study aims to uncover patterns in users' trip planning, focusing on how they weigh sustainability against
                            other priorities like cost and convenience. Through visualization strategies and nudging techniques,
                            the application is expected to foster eco-friendly travel choices, leading to a broader understanding
                            of how digital tools can support sustainable tourism. Additionally, the insights will contribute to
                            sustainable travel practices by addressing user behavior and its impact on tourism sustainability.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Limitations</Typography>
                        <Typography level='body-sm'>
                            The application is limited by the availability of data, which is primarily sourced from third-party providers.
                            This includes the number of cities, connections, popularity, seasonality data, and etc. The application is also
                            limited by the scope of the study, which is focused on the decision-making processes of users within the context
                            of sustainable tourism. This means that the application does not take into account other factors that may
                            influence user behaviors, preferences, and attitudes. These include the political, economic, and cultural
                            contexts of the users, as well as the broader social and environmental implications of their decisions.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>User Surveys and Insights Collection</Typography>
                        <Typography level='body-sm'>
                            To gain deeper insights into user preferences and travel planning behaviors, the application will include optional surveys.
                            These surveys aim to gather data on users’ decision-making processes, including how they prioritize sustainability,
                            cost, convenience, and other factors when planning trips. All survey responses will be anonymized to
                            protect individual identities and will be used only to improve the application’s ability to support
                            sustainable travel choices.
                            <br/>
                            <br/>
                            <Typography sx={{fontWeight:'700'}}>
                            The survey is open now. Click here to participate: <a target='blank' href='https://forms.gle/YL1dKEpB4s518g6N7'>User Survey</a>

                            </Typography>
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Privacy and Data Security</Typography>
                        <Typography level='body-sm'>
                            The application places a high priority on user privacy and data security. Users creating personal
                            accounts to save trips and customize their experience can trust that their data will be securely stored
                            and managed according to stringent data protection standards, including compliance with GDPR. Any data collected
                            for research purposes will be anonymized and aggregated, and users will be fully informed about what data is being
                            collected and how it will be used.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Account Deletion</Typography>
                        <Typography level='body-sm'>
                            For users wishing to delete their account, an easy-to-navigate option is available. By visiting the “Trips” page and
                            scrolling to the bottom, users will find the "Delete Account" option. This allows for immediate deletion of all
                            personal data associated with the account, providing users full control over their information and a secure, transparent way to manage their data.
                        </Typography>
                    </Box>

                    <Box my={2}>
                        <Typography level='h4'>Credits</Typography>
                        <Typography level='body-sm'>
                            The application is deployed on Firebase and is developed using the following technologies:{' '}
                            <a href='https://reactjs.org/'>React</a>, <a href='https://mui.com/'>Material-UI</a>,{' '}
                            <a href='https://react-leaflet.js.org/'>React-Leaflet</a>,{' '}
                            <a href='https://react-icons.github.io/react-icons/'>React-Icons</a>.
                        </Typography>
                        <Typography level='body-sm'>
                            The images used in the application are sourced from <a href='https://www.pexels.com/'>Pexels</a> and{' '}
                            <a href='https://unsplash.com/'>Unsplash</a>.
                        </Typography>
                        <Typography level='body-sm'>
                            TravelGreen by <a href='https://www.linkedin.com/in/saiyam3243/'>Saiyam Jain</a>.
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default About;
